import Image from 'next/image'
import { useEffect, useState } from 'react'
import clsx from 'clsx'

import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'

import { RubrikObservasiPraktikKinerjaDialog } from 'app/KinerjaShared/components/RubrikObservasiPraktikKinerjaDialog'

import type { RubrikObservasiPraktikKinerjaDialogProps } from 'app/KinerjaShared/components/RubrikObservasiPraktikKinerjaDialog'

export type IndikatorPraktikKinerjaCardProps = {
  illustrationPath?: string
  className?: string
  title: string
  targetKualitas: string
  targetPerilakuList?: RubrikObservasiPraktikKinerjaDialogProps['targetPerilakuList']
  isLoading?: boolean
  onRubrikDialogOpened?: () => void
  onRubrikDialogClosed?: () => void
}

export function IndikatorPraktikKinerjaCard({
  illustrationPath = '',
  className = '',
  title,
  targetKualitas,
  targetPerilakuList,
  isLoading = false,
  onRubrikDialogOpened = () => {},
  onRubrikDialogClosed = () => {},
}: IndikatorPraktikKinerjaCardProps) {
  // -----------------------------------------
  // Handle rubrik dialog
  // -----------------------------------------
  const [dialogOpen, setDialogOpen] = useState(false)

  const handleCloseRubrikDialog = () => {
    onRubrikDialogClosed()
    setDialogOpen(false)
  }

  useEffect(() => {
    if (dialogOpen) {
      onRubrikDialogOpened()
    }
  }, [dialogOpen, onRubrikDialogOpened])

  return (
    <section
      className={clsx(
        'flex space-x-4 border border-solid rounded-lg p-4 max-w-4xl border-subdued',
        className
      )}
    >
      <div className="flex space-x-4 max-w-md w-full">
        <div className="w-20 h-20 items-center flex">
          {isLoading ? (
            <Shimmer width={84} height={84} />
          ) : (
            <Image width={84} height={84} src={illustrationPath} alt="" />
          )}
        </div>
        <div className="w-full space-y-1">
          <h3 className="space-y-1">
            <Text
              color="subdued"
              variant="body-sm"
              as="span"
              className="!block"
            >
              Indikator pilihan:
            </Text>
            {isLoading ? (
              <Shimmer className="!h-6 !w-full" />
            ) : (
              <Text
                variant="action"
                className="!block overflow-wrap-anywhere"
                as="span"
              >
                {title}
              </Text>
            )}
          </h3>
          <button
            type="button"
            className="flex items-center cursor-pointer"
            aria-haspopup={true}
            aria-expanded={dialogOpen}
            onClick={() => {
              setDialogOpen(true)
            }}
          >
            <Text color="primary" variant="body-sm-bold">
              Cek Rubrik Observasi
            </Text>
            <Icon color="informational">chevron_right</Icon>
          </button>
        </div>
      </div>

      <p className="max-w-md w-full space-y-1">
        <Text color="subdued" variant="body-sm" as="span" className="!block">
          Praktiknya dilihat dari:
        </Text>

        {isLoading ? (
          <div className="space-y-1 w-full">
            <Shimmer noOfLines={3} className="!w-full" />
          </div>
        ) : (
          <Text variant="body-sm" className="overflow-wrap-anywhere !block">
            {targetKualitas}
          </Text>
        )}
      </p>

      <RubrikObservasiPraktikKinerjaDialog
        isOpen={dialogOpen}
        isLoading={isLoading}
        onClose={handleCloseRubrikDialog}
        illustrationPath={illustrationPath}
        indikatorTitle={title}
        targetKualitas={targetKualitas}
        targetPerilakuList={targetPerilakuList}
      />
    </section>
  )
}
